// useRadarResults — alertas RADAR · Estrategia 1 (EG1 PDF) con resultado.
// Fuente ÚNICA: RTDB /radar_vip_results (escrito por firebase_upload +
// resuelto por edge-elite-radar-sweep). NUNCA se mezcla con
// signals_history: son canales paralelos y sus números van SIEMPRE
// separados (decisión Matías 2026-09-03).
//
// Normalización por alerta: fecha = día calendario Chile del disparo
// (ts; fallback resolved_at), partido con visitante primero, ventana
// Q2/Q3/Q4m1/Q4m6, side desde direction, won solo si resolved.
// Sin yield ni unidades en toda esta vista: solo acierto %, W-L, conteos.
import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { ref, onValue } from 'firebase/database';

const TZ = 'America/Santiago';

export function santiagoDayRadar(date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);
}

// Lunes–domingo (hora Chile) de la semana actual + offset (0 = en curso,
// -1 = anterior). Ancla a mediodía UTC para que el aritmético de días no
// salte con DST.
export function chileWeekDates(offsetWeeks = 0) {
  const base = new Date(`${santiagoDayRadar(new Date())}T12:00:00Z`);
  const dow = (base.getUTCDay() + 6) % 7; // 0 = lunes
  const monday = base.getTime() + (offsetWeeks * 7 - dow) * 86400000;
  return Array.from({ length: 7 }, (_, i) =>
    new Date(monday + i * 86400000).toISOString().slice(0, 10));
}

// Conteos sobre alertas RESUELTAS. Las pendientes quedan fuera del
// denominador (se muestran como "En curso" en la lista, nada más).
export function radarStats(list) {
  let w = 0, l = 0;
  for (const a of list) {
    if (!a.resolved) continue;
    if (a.won) w++; else l++;
  }
  const n = w + l;
  return { w, l, n, pct: n > 0 ? (w / n) * 100 : null };
}

// Ranking "Ligas con mejores resultados esta semana": semana lun–dom
// Chile, mínimo 3 alertas resueltas, orden acierto % desc (empate: más
// volumen primero). Top 8.
export function radarWeekRanking(alerts, offsetWeeks = 0, top = 8) {
  const dias = new Set(chileWeekDates(offsetWeeks));
  const porLiga = new Map();
  for (const a of alerts) {
    if (!a.resolved || !dias.has(a.fecha)) continue;
    const e = porLiga.get(a.liga) || { liga: a.liga, w: 0, l: 0 };
    if (a.won) e.w++; else e.l++;
    porLiga.set(a.liga, e);
  }
  return [...porLiga.values()]
    .map((e) => ({ ...e, n: e.w + e.l, pct: (e.w / (e.w + e.l)) * 100 }))
    .filter((e) => e.n >= 3)
    .sort((x, y) => y.pct - x.pct || y.n - x.n)
    .slice(0, top);
}

export default function useRadarResults() {
  const [raw, setRaw] = useState(null);

  useEffect(() => {
    const unsub = onValue(ref(db, 'radar_vip_results'), (snap) => {
      setRaw(snap.val() || {});
    }, () => setRaw({}));
    return () => unsub();
  }, []);

  const alerts = useMemo(() => {
    if (!raw) return [];
    const out = [];
    for (const [gid, node] of Object.entries(raw)) {
      if (!node || typeof node !== 'object') continue;
      for (const [wkey, f] of Object.entries(node)) {
        if (!f || typeof f !== 'object') continue;
        const when = f.ts || f.resolved_at;
        const ts = when ? Date.parse(when) : 0;
        if (!ts) continue;
        const edge = parseFloat(f.edge);
        out.push({
          id: `${gid}_${wkey}`,
          gid,
          fecha: santiagoDayRadar(new Date(ts)),
          ts,
          liga: f.liga || '—',
          partido: `${f.away || '?'} @ ${f.home || '?'}`,
          ventana: wkey.replace('_m', 'm'), // Q2 | Q3 | Q4m1 | Q4m6
          edge: Number.isNaN(edge) ? null : edge,
          linea: f.linea ?? null,
          side: f.direction === 'OVER' ? 'OVER' : 'UNDER',
          resolved: f.resolved === true,
          won: f.resolved === true && f.won === true,
          total_final: f.total_final ?? null,
        });
      }
    }
    out.sort((a, b) => b.ts - a.ts);
    return out;
  }, [raw]);

  return { alerts, loading: raw === null };
}
